// T1 · File complète — liveQuery des commandes ouvertes + leurs lignes. Par
// commande : badge GROUPÉ (LOT 7 §2 : À préparer / En cours / Prête) + badge
// détaillé conservé, heure, badge ×N si train partagé, et DÉTAIL INCONDITIONNEL
// (LOT 7 §5 : nom + qty sur chaque ligne, modifs + note rattachées à SA pizza —
// jamais besoin de la Loupe). Actions = EXACTEMENT la matrice (via
// useT1ActionGate, « Commencer » = commandee→priorisee). Aucune logique de
// transition ici (INV-B). « Annuler » (6b.2) = outline destructive + overlay
// maison de confirmation, écriture au confirm seul.
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCatalogue } from '@/hooks/useCatalogue';
import { useFileCommandes } from '@/hooks/useFileCommandes';
import { useMinuteTick } from '@/hooks/useMinuteTick';
import { useNumerosService } from '@/hooks/useNumerosService';
import {
  LIBELLE_GROUPE,
  LIBELLE_STATUT,
  useT1ActionGate,
} from '@/hooks/useT1ActionGate';
import { nomAffichage } from '@/lib/affichage-commande';
import { formatHeure, formatMod } from '@/lib/format';
import { evalDelais, versChargeCommandes } from '@/lib/heure-capacite';
import { CAMION_CAPACITE_CONFIG } from '@/config/camion.pizznco';
import { parseNotes } from '@/lib/notes-convention';
import { pluriel } from '@/lib/pluriel';
import { NOTE_MAX_TUILE, NotePizza } from '@/screens/shared/NotePizza';
import { StatutDelai } from '@/screens/shared/StatutDelai';
import { AnnulerCommandeOverlay } from './AnnulerCommandeOverlay';
import type { Commande, CommandePizza } from '@/db/schema';

interface Props {
  onLoupe: (commandeId: number) => void;
  onMonnaie: (commandeId: number) => void;
}

function nbPizzas(lignes: CommandePizza[] | undefined): number {
  return (lignes ?? []).reduce((s, l) => s + l.qty, 0);
}

export function FileView({ onLoupe, onMonnaie }: Props) {
  const { commandes, lignesParCommande } = useFileCommandes();
  const gate = useT1ActionGate();
  // Numéro de service (complément 7c) : rang de création, service courant.
  const numeros = useNumerosService();
  const catalogue = useCatalogue();
  const nomParId = new Map(catalogue.map((p) => [p.id as number, p.nom]));

  // Commande dont l'annulation est en cours de confirmation (6b.2). Aucune
  // écriture tant que l'overlay n'est pas confirmé.
  const [annulerId, setAnnulerId] = useState<number | null>(null);
  const commandeAAnnuler =
    annulerId !== null
      ? (commandes.find((c) => c.id === annulerId) ?? null)
      : null;

  // Compte des commandes par train (pour le badge ×N).
  const parTrain = new Map<number, number>();
  for (const c of commandes) {
    if (c.train_id !== undefined) {
      parTrain.set(c.train_id, (parTrain.get(c.train_id) ?? 0) + 1);
    }
  }

  // Messages de délai ATTACHÉS aux commandes (§2 amendé — cascade 4 états,
  // projection pure du moteur ; prete/terminales n'ont aucune entrée),
  // rafraîchis à la minute pleine (tick — sans navigation ni interaction).
  const now = useMinuteTick();
  const delais = evalDelais(
    now,
    versChargeCommandes(commandes, lignesParCommande),
    CAMION_CAPACITE_CONFIG,
  );

  return (
    <div className="flex flex-col gap-3 p-4">
      {commandes.length === 0 && (
        <p className="font-jost text-muted-foreground">File vide.</p>
      )}

      {commandes.map((c: Commande) => {
        const id = c.id as number;
        const trainN =
          c.train_id !== undefined ? (parTrain.get(c.train_id) ?? 0) : 0;
        return (
          <Card key={id} data-testid="file-commande">
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge>{LIBELLE_GROUPE[c.statut]}</Badge>
                  {LIBELLE_GROUPE[c.statut] === 'En cours' && (
                    <Badge variant="secondary">{LIBELLE_STATUT[c.statut]}</Badge>
                  )}
                  {trainN > 1 && (
                    <Badge
                      variant="secondary"
                      aria-label={`Train de ${trainN} commandes`}
                    >
                      ×{trainN}
                    </Badge>
                  )}
                </div>
                <span className="font-mono text-xl">
                  {formatHeure(c.heure_retrait_proposee)}
                </span>
              </div>

              <StatutDelai delai={delais.get(id)} className="text-lg" />

              <div className="font-jost">
                <span
                  className={
                    c.client_nom_snapshot
                      ? 'font-cormorant text-xl italic'
                      : 'text-muted-foreground'
                  }
                >
                  {nomAffichage(c, numeros)}
                </span>
                <span className="text-muted-foreground">
                  {' '}
                  · {nbPizzas(lignesParCommande.get(id))}{' '}
                  {pluriel(nbPizzas(lignesParCommande.get(id)), 'pizza')}
                </span>
              </div>

              {/* Détail INCONDITIONNEL (LOT 7 §5) : chaque ligne = nom + qty,
                  caractères francs non italiques ; modifs + note de SA pizza. */}
              {(lignesParCommande.get(id) ?? []).map((l) => {
                const parsed = parseNotes(l.notes_libres ?? '');
                return (
                  <div
                    key={l.id}
                    data-testid="file-ligne"
                    className="flex flex-wrap items-baseline gap-2"
                  >
                    <span className="font-jost text-lg font-medium">
                      {l.qty}× {nomParId.get(l.pizzaId) ?? `Pizza #${l.pizzaId}`}
                    </span>
                    {parsed.mod.map((m, i) => (
                      <span
                        key={`${m}-${i}`}
                        className="rounded bg-rose px-2 py-0.5 font-jost text-sm text-ardoise"
                      >
                        {formatMod(m)}
                      </span>
                    ))}
                    <NotePizza texte={parsed.texte} max={NOTE_MAX_TUILE} />
                  </div>
                );
              })}

              {/* Actions = matrice exacte. « Annuler » (destructive) : outline
                  réduit + confirmation — la transition reste celle du gate. */}
              <div className="flex flex-wrap gap-2">
                {gate.actionsPour(c.statut).map((a) =>
                  a.destructive ? (
                    <Button
                      key={a.cible}
                      type="button"
                      variant="outline"
                      onClick={() => setAnnulerId(id)}
                      className="min-h-[64px] min-w-[120px] border-destructive bg-transparent text-lg text-destructive hover:bg-transparent hover:text-destructive"
                    >
                      {a.libelle}
                    </Button>
                  ) : (
                    <Button
                      key={a.cible}
                      type="button"
                      onClick={() => void gate.executer(id, a.cible)}
                      className="min-h-[80px] min-w-[120px] text-lg"
                    >
                      {a.libelle}
                    </Button>
                  ),
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onLoupe(id)}
                  className="min-h-[64px] min-w-[120px] text-lg"
                >
                  Loupe
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onMonnaie(id)}
                  className="min-h-[64px] min-w-[120px] text-lg"
                >
                  Rendu monnaie
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {commandeAAnnuler && (
        <AnnulerCommandeOverlay
          nbPizzas={nbPizzas(
            lignesParCommande.get(commandeAAnnuler.id as number),
          )}
          clientNom={nomAffichage(commandeAAnnuler, numeros)}
          onRetour={() => setAnnulerId(null)}
          onConfirmer={() => {
            void gate
              .executer(commandeAAnnuler.id as number, 'annulee')
              .then(() => setAnnulerId(null));
          }}
        />
      )}
    </div>
  );
}
